import { validateRolesASI, validateRolesIIP } from '../Custom/Custom';

const IIP_ONLY_IDENTITIES = [
  'HQIIP', 'HQIIPM', 'HQIIPE', 'IIPMINE', 'HQIIPMINE', 'IIPELEC', 'HQIIPELEC',
  'DISIIP', 'DIVIIP', 'FACTOIIP'
];

const isIIPIdentity = (decodedToken) => {
  if (!decodedToken?.id) return false;
  const roles = String(decodedToken.id.ROLE_CODE || '')
    .split('|')
    .map((r) => r.trim().toUpperCase())
    .filter(Boolean);
  const userId = String(decodedToken.id.USER_ID || '').trim().toUpperCase();
  const userName = String(decodedToken.id.USER_NAME || '').trim().toUpperCase();
  return (
    IIP_ONLY_IDENTITIES.includes(userId) ||
    IIP_ONLY_IDENTITIES.includes(userName) ||
    roles.some((role) => IIP_ONLY_IDENTITIES.includes(role))
  );
};

/**
 * Determines which dashboard components to show based on user roles
 * @param {Object} decodedToken - Decoded JWT token with user information
 * @returns {Object} Object containing boolean flags for each component
 */
export const getDashboardComponents = async (decodedToken) => {
  if (!decodedToken || !decodedToken.id) {
    return {
      showASI: false,
      showIIP: false,
      showPriceWages: false,
      showAgriculture: false,
      showGVA: false,
      showAll: false
    };
  }

  const roles = decodedToken.id.ROLE_CODE.split('|');

  // Check for SUPER ADMIN - shows all components
  if (decodedToken.id.ROLE_CODE === 'SUPER' && decodedToken.id.USER_TYPE_CODE === '0') {
    return {
      showASI: true,
      showIIP: true,
      showPriceWages: true,
      showAgriculture: true,
      showGVA: true,
      showAll: true
    };
  }

  // Check ASI permissions
  const hasASIAccess = await validateRolesASI(decodedToken);
  
  // Check IIP permissions (HQIIP + Mining/Electricity users)
  const hasIIPAccess = (await validateRolesIIP(decodedToken)) || isIIPIdentity(decodedToken);
  
  // Check if user has FACTO role - if yes, hide IIP from dashboard
  const isFACTO = roles.some(role => role === 'FACTO');
  const finalIIPAccess = isFACTO ? false : hasIIPAccess;
  
  // Check Price & Wages permissions (based on role codes)
  const hasPriceWagesAccess = roles.some(role => 
    ['HQPRI', 'HQADM', 'DIVADM', 'DIVMPR', 'DISPRI', 'DISADM'].includes(role)
  );
  
  // Check Agriculture permissions (based on role codes)
  const hasAgricultureAccess = roles.some(role => 
    ['HQADM', 'DIVADM', 'DISADM'].includes(role)
  );

  // Check GVA permissions (based on role codes) - similar to ASI access
  const hasGVAAccess = roles.some(role => 
    ['HQADM', 'DIVADM', 'DISADM', 'HQPRI', 'DIVMPR', 'DISPRI'].includes(role)
  );

  return {
    showASI: hasASIAccess,
    showIIP: finalIIPAccess,
    showPriceWages: hasPriceWagesAccess,
    showAgriculture: hasAgricultureAccess,
    showGVA: hasGVAAccess,
    showAll: false
  };
};

/**
 * Get user role type for display purposes
 * @param {Object} decodedToken - Decoded JWT token
 * @returns {String} User role type
 */
export const getUserRoleType = (decodedToken) => {
  if (!decodedToken || !decodedToken.id) {
    return 'Unknown';
  }

  const userLevel = decodedToken.id.USER_TYPE;
  const roles = decodedToken.id.ROLE_CODE.split('|');

  // SUPER ADMIN
  if (decodedToken.id.ROLE_CODE === 'SUPER' && decodedToken.id.USER_TYPE_CODE === '0') {
    return 'SUPER ADMIN USER';
  }

  // ASI User
  if (roles.some(role => ['HQASI', 'DIVASI', 'DISASI', 'FACTO'].includes(role))) {
    return 'ASI USER';
  }

  // IIP User (HQIIP + Mining/Electricity dedicated users)
  if (
    isIIPIdentity(decodedToken) ||
    roles.some(role =>
      ['HQIIP', 'DISIIP', 'DIVIIP', 'FACTOIIP', 'HQIIPM', 'HQIIPE', 'IIPMine', 'IIPElec', 'HQIIPMine', 'HQIIPElec'].includes(role)
    )
  ) {
    return 'IIP USER';
  }

  // Price & Wages User
  if (roles.some(role => ['HQPRI', 'DIVMPR', 'DISPRI'].includes(role))) {
    return 'PRICE & WAGES USER';
  }

  // Admin User
  if (roles.some(role => ['HQADM', 'DIVADM', 'DISADM'].includes(role))) {
    return 'ADMIN USER';
  }

  return userLevel || 'Unknown';
};
